import { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { AuthProvider }      from './context/AuthContext';
import { supabase }          from './lib/supabase';
import { QUERY_KEYS }        from './lib/queryKeys';
import Footer                from './components/layout/Footer/Footer';
import PrivateRoute          from './components/layout/PrivateRoute/PrivateRoute';
import Home                  from './pages/Home/Home';
import './App.css';

// Lazy-load componentes que usan framer-motion para sacarlo del bundle critico
const Navbar              = lazy(() => import('./components/layout/Navbar/Navbar'));
const AnnouncementBanner  = lazy(() => import('./components/layout/AnnouncementBanner/AnnouncementBanner'));
const WhatsAppButton      = lazy(() => import('./components/layout/WhatsAppButton/WhatsAppButton'));
const ScrollToTop         = lazy(() => import('./components/layout/ScrollToTop/ScrollToTop'));
const ExitPopup           = lazy(() => import('./components/layout/ExitPopup/ExitPopup'));
const IdleTimerWrapper    = lazy(() => import('./components/layout/IdleTimerWrapper/IdleTimerWrapper'));
const PageLoader          = lazy(() => import('./components/layout/PageLoader/PageLoader'));
const Programa          = lazy(() => import('./pages/Programa/Programa'));
const Contacto          = lazy(() => import('./pages/Contacto/Contacto'));
const Noticias          = lazy(() => import('./pages/Noticias/Noticias'));
const NoticiaDetalle    = lazy(() => import('./pages/Noticias/NoticiaDetalle'));
const Login             = lazy(() => import('./pages/Auth/Login'));
const Registro          = lazy(() => import('./pages/Auth/Registro'));
const RecuperarPassword = lazy(() => import('./pages/Auth/RecuperarPassword'));
const ResetPassword     = lazy(() => import('./pages/Auth/ResetPassword'));
const Dashboard         = lazy(() => import('./pages/Dashboard/Dashboard'));
// Páginas protegidas adicionales — perfil y reseñas
const MisResenas        = lazy(() => import('./pages/MisResenas/MisResenas'));
const Cuenta            = lazy(() => import('./pages/Cuenta/Cuenta'));
// Páginas nuevas del prototipo
const ConveniosPage     = lazy(() => import('./pages/Convenios/ConveniosPage'));
const Galeria           = lazy(() => import('./pages/Galeria/Galeria'));
const Cinfop            = lazy(() => import('./pages/Cinfop/Cinfop'));
const ProgramaCinfop    = lazy(() => import('./pages/Cinfop/ProgramaCinfop'));
const EquipoPage        = lazy(() => import('./pages/Equipo/EquipoPage'));
const TestimoniosPage   = lazy(() => import('./pages/Testimonios/TestimoniosPage'));
const Admin             = lazy(() => import('./pages/Admin/Admin'));
const FAQPage           = lazy(() => import('./pages/FAQ/FAQPage'));
const BolsaEmpleo      = lazy(() => import('./pages/BolsaEmpleo/BolsaEmpleo'));
const QuienesSomosPage = lazy(() => import('./pages/QuienesSomos/QuienesSomosPage'));
const Kids              = lazy(() => import('./pages/Kids/Kids'));
const PruebaUbicacion   = lazy(() => import('./pages/PruebaUbicacion/PruebaUbicacion'));
const Financiamiento    = lazy(() => import('./pages/Financiamiento/Financiamiento'));
const Terminos          = lazy(() => import('./pages/Terminos/Terminos'));
const Privacidad        = lazy(() => import('./pages/Privacidad/Privacidad'));
const NotFound          = lazy(() => import('./pages/NotFound/NotFound'));

function Loading() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-soft)',
    }}>
      <div style={{
        width: 44, height: 44,
        border: '3px solid rgba(32,32,160,.2)',
        borderTop: '3px solid var(--color-primary)',
        borderRadius: '50%',
        animation: 'spinAnim .7s linear infinite',
      }} />
    </div>
  );
}

// Rutas donde NO se muestra Navbar/Footer/WhatsApp/Banner (tienen su propio layout)
const AUTH_PATHS = ['/auth/', '/dashboard', '/admin'];

// Componente que sube al inicio al cambiar de ruta
function RouteScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();

  const isAuthOrDashboard = AUTH_PATHS.some(p => location.pathname.startsWith(p));
  const isKids = location.pathname.startsWith('/kids');
  // Landing CONAPE — trae su propio header estático y CTA sticky; oculta el chrome global.
  const isLanding = location.pathname.startsWith('/financiamiento');

  /* Estado reactivo del banner — se actualiza al cerrar */
  const [bannerOpen, setBannerOpen] = useState(() => !sessionStorage.getItem('ae_banner_closed'));
  const handleBannerClose = useCallback(() => setBannerOpen(false), []);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--banner-height', bannerOpen ? '44px' : '0px');
  }, [bannerOpen]);


  // Prefetch datos críticos del Home al iniciar — carrusel, reseñas y noticias
  const qc = useQueryClient();
  useEffect(() => {
    qc.prefetchQuery({
      queryKey: QUERY_KEYS.carousel,
      queryFn: () => supabase
        .from('carousel')
        .select('id, imagen_url, titulo, subtitulo, orden')
        .eq('activo', true)
        .order('orden', { ascending: true })
        .then(({ data }) => data || []),
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
    });
    qc.prefetchQuery({
      queryKey: QUERY_KEYS.noticiasPreview,
      queryFn: () => supabase
        .from('noticias')
        .select('id,slug,titulo,resumen,categoria,imagen_url,created_at')
        .eq('publicada', true)
        .order('created_at', { ascending: false })
        .limit(3)
        .then(({ data }) => data || []),
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!isAuthOrDashboard && (
        <Suspense fallback={null}>
          <PageLoader key="page-loader" />
          {!isKids && !isLanding && <AnnouncementBanner onClose={handleBannerClose} />}
          <Navbar />
        </Suspense>
      )}

      <RouteScrollToTop />
      <main>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Rutas públicas principales */}
            <Route path="/"                  element={<Home />}           />
            <Route path="/programa"          element={<Programa />}       />
            <Route path="/contacto"          element={<Contacto />}       />
            <Route path="/noticias"          element={<Noticias />}       />
            <Route path="/noticias/:slug"    element={<NoticiaDetalle />} />

            {/* Rutas de autenticación */}
            <Route path="/auth/login"      element={<Login />}             />
            <Route path="/auth/registro"   element={<Registro />}          />
            <Route path="/auth/recuperar"  element={<RecuperarPassword />} />
            <Route path="/auth/reset"     element={<ResetPassword />}     />
            <Route path="/auth/callback"  element={<ResetPassword />}     />

            {/* Dashboard — ruta protegida */}
            <Route path="/dashboard/*" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />

            {/* Rutas protegidas con Navbar/Footer visibles */}
            <Route path="/mis-resenas" element={
              <PrivateRoute>
                <MisResenas />
              </PrivateRoute>
            } />
            <Route path="/cuenta" element={
              <PrivateRoute>
                <Cuenta />
              </PrivateRoute>
            } />

            {/* Páginas públicas nuevas */}
            <Route path="/convenios"  element={<ConveniosPage />} />
            <Route path="/galeria"    element={<Galeria />}       />
            <Route path="/testimonios" element={<TestimoniosPage />} />
            <Route path="/cinfop"     element={<Cinfop />}        />
            <Route path="/cinfop/:slug" element={<ProgramaCinfop />} />
            <Route path="/equipo"    element={<EquipoPage />}    />
            <Route path="/preguntas-frecuentes" element={<FAQPage />} />
            <Route path="/prueba-ubicacion" element={<PruebaUbicacion />} />
            <Route path="/bolsa-empleo" element={<BolsaEmpleo />} />
            <Route path="/quienes-somos" element={<QuienesSomosPage />} />
            <Route path="/financiamiento" element={<Financiamiento />} />
            <Route path="/conape"      element={<Navigate to="/financiamiento" replace />} />
            <Route path="/kids"        element={<Kids />}          />
            <Route path="/terminos"    element={<Terminos />}      />
            <Route path="/privacidad"  element={<Privacidad />}    />

            {/* Panel Admin — solo accesible para usuarios con rol 'admin' */}
            <Route path="/admin/*" element={
              <PrivateRoute requireRole="admin">
                <Admin />
              </PrivateRoute>
            } />

            {/* Catch-all — pagina 404 para rutas no definidas */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {!isAuthOrDashboard && (
        <Suspense fallback={null}>
          <Footer />
          {/* La landing CONAPE usa su propio CTA sticky de WhatsApp, no el botón flotante global */}
          {!isLanding && <WhatsAppButton />}
          <ScrollToTop />
          {!isKids && !isLanding && <ExitPopup />}
        </Suspense>
      )}
      <Suspense fallback={null}>
        <IdleTimerWrapper />
      </Suspense>
    </>
  );
}

// Cliente de React Query — caché global para todas las consultas
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,     // 5 minutos — datos frescos sin refetch
      gcTime: 1000 * 60 * 10,       // 10 minutos en memoria (v5: gcTime, no cacheTime)
      refetchOnWindowFocus: false,   // no recargar al cambiar de tab
      refetchOnMount: false,         // no refetch si datos en caché
      retry: 1,                      // solo 1 reintento en error
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppShell />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </QueryClientProvider>
  );
}
